import { ArrowRight, Crosshair, ShieldCheck, Layers, BadgeDollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionLabel, CTAButton } from "./primitives";
import { GridField } from "./decor";

const SPECTRUM = ["Engineering", "Product", "Operations", "Leadership", "Sales"];

const DIFFS = [
  {
    icon: Crosshair,
    title: "Operator-Led Recruiting",
    desc: "We screen candidates against real, hands-on requirements — not keyword matches. Every profile is read by an experienced operator who has actually done the job.",
  },
  {
    icon: ShieldCheck,
    title: "Evidence-Based Scorecard",
    desc: "Every candidate is validated against a clear, evidence-based role scorecard before they reach your desk — de-risking the hire.",
  },
  {
    icon: Layers,
    title: "Full-Spectrum, One Partner",
    desc: "Engineering, product, operations, leadership and sales hiring under one roof — consistent quality and one point of accountability.",
  },
  {
    icon: BadgeDollarSign,
    title: "Pay Only on Success",
    desc: "A transparent success-placement-fee model with a replacement guarantee. No retainers, no upfront risk.",
  },
];

export const Recruitment = ({ showLink = true }) => (
  <section id="recruitment" data-testid="recruitment-section" className="relative isolate bg-[#F4F6F0] py-24 md:py-32">
    <GridField className="opacity-[0.04]" />
    <div className="mx-auto max-w-7xl px-6 md:px-8">
      {/* Header */}
      <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
        <Reveal>
          <SectionLabel>Recruitment Services</SectionLabel>
          <h2 className="mt-4 max-w-3xl font-serif text-3xl font-medium tracking-tight text-[#1b1f17] sm:text-4xl lg:text-5xl">
            Hire the people who power your growth.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-sm leading-relaxed text-[#5f6857] lg:text-right">
            End-to-end hiring support across your core functions — engineering, product,
            operations, leadership and sales — delivered with the rigor of a partner, not a
            placement agency.
          </p>
          {showLink && (
            <Link
              to="/recruitment"
              data-testid="recruit-view-details"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#5C8A1E] transition-colors hover:text-[#4a7418] lg:justify-end"
            >
              View our recruitment practice <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </Reveal>
      </div>

      {/* The spectrum */}
      <Reveal delay={0.05}>
        <div className="mt-10 flex flex-wrap gap-3">
          {SPECTRUM.map((s, i) => (
            <span
              key={i}
              data-testid={`spectrum-${i}`}
              className="rounded-full border border-brand/40 bg-brand/5 px-5 py-2 text-sm font-semibold text-[#5C8A1E]"
            >
              {s}
            </span>
          ))}
        </div>
      </Reveal>

      {/* Why we stand out */}
      <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {DIFFS.map((d, i) => {
          const Icon = d.icon;
          return (
            <Reveal key={i} delay={i * 0.08}>
              <div
                data-testid={`recruit-diff-${i}`}
                className="group h-full rounded-2xl border border-line bg-white p-7 shadow-[0_6px_28px_rgba(28,38,18,0.06)] transition-transform duration-300 hover:-translate-y-1 hover:border-brand/50"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl border border-line text-[#5C8A1E] transition-colors group-hover:border-brand/40 group-hover:bg-brand/10">
                  <Icon className="h-6 w-6" strokeWidth={1.8} />
                </span>
                <h4 className="mt-5 font-serif text-lg font-semibold leading-snug text-[#1b1f17]">{d.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-[#5f6857]">{d.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* Engagement / commercial model */}
      <Reveal delay={0.1}>
        <div
          data-testid="recruit-model"
          className="mt-20 rounded-3xl border border-brand/30 bg-white p-8 shadow-[0_0_50px_rgba(140,198,63,0.10)] md:p-12"
        >
          <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5C8A1E]">
                <BadgeDollarSign className="h-3.5 w-3.5" /> Commercial Model
              </span>
              <h3 className="mt-5 font-serif text-2xl font-medium tracking-tight text-[#1b1f17] sm:text-3xl">
                Success-Based Placement
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#4c5445] sm:text-base">
                We work on a success-fee model — you pay a percentage of the placed candidate's
                first-year compensation, and only on successful placement. No retainers, no upfront
                risk.
              </p>
              <p className="mt-4 text-sm italic text-[#828c75]">
                Fee structure and terms tailored after a brief conversation.
              </p>
            </div>
            <ul className="space-y-3 rounded-2xl border border-line bg-[#F4F6F0] p-6">
              {[
                "Pay only on successful placement",
                "Replacement guarantee included",
                "No retainer or upfront fees",
                "Dedicated account partner per search",
              ].map((point, i) => (
                <li key={i} data-testid={`model-point-${i}`} className="flex items-start gap-3 text-sm text-[#4c5445]">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#5C8A1E]" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-8 border-t border-line pt-8">
            <CTAButton testId="recruit-cta" href="#contact" className="w-full sm:w-auto">
              Start a Search <ArrowRight className="h-4 w-4" />
            </CTAButton>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
